import React from 'react';

/**
 * E-mail escrito como texto puro vira link `mailto:` na leitura.
 *
 * O conteúdo migrado traz endereços de e-mail como texto (composição do
 * Conselho Superior, unidades descentralizadas da Corregedoria, legislação), e
 * a carga não cria o link — apontamentos N18, N26 e N36 (AB#11165). Em vez de
 * reescrever o conteúdo em cada carga, a `view` do bloco de texto recebe uma
 * cópia do valor com cada e-mail envolto num elemento `link` do volto-slate
 * (forma `data.link.email`, a mesma que o editor grava quando o redator
 * escolhe "e-mail" na caixa de link). O valor salvo não muda; na canvas do
 * editor o texto continua como foi escrito.
 *
 * Um e-mail já dentro de um link é deixado como está, e as marcas da folha
 * (negrito, itálico) são preservadas nos pedaços de texto.
 */
export const EMAIL_RE =
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;

const LINK_TYPES = new Set(['link', 'a']);

type Leaf = { text: string; [marca: string]: unknown };
type Elemento = { type?: string; children?: Node[]; [k: string]: unknown };
type Node = Leaf | Elemento;

const isLeaf = (n: Node): n is Leaf => typeof (n as Leaf).text === 'string';

/**
 * O renderizador de link do volto-slate em uso no portal (plugins/Link) lê o
 * destino em `data.url`; a caixa de link do editor grava também
 * `data.link.email`. Os dois ficam preenchidos para que o nó renderize igual
 * com qualquer dos dois renderizadores (em 1.3.0 só `link.email` saía como
 * `href="/"` em produção).
 */
const linkDeEmail = (leaf: Leaf, email: string): Elemento => ({
  type: 'link',
  data: {
    url: `mailto:${email}`,
    link: { email: { email_address: email } },
  },
  children: [{ ...leaf, text: email }],
});

/** Divide uma folha nos e-mails que contém; devolve `null` se não há nenhum. */
export function dividirFolha(leaf: Leaf): Node[] | null {
  const texto = leaf.text;
  const pedacos: Node[] = [];
  let fim = 0;
  for (const m of texto.matchAll(EMAIL_RE)) {
    const ini = m.index ?? 0;
    if (ini > fim) pedacos.push({ ...leaf, text: texto.slice(fim, ini) });
    pedacos.push(linkDeEmail(leaf, m[0]));
    fim = ini + m[0].length;
  }
  if (!pedacos.length) return null;
  if (fim < texto.length) pedacos.push({ ...leaf, text: texto.slice(fim) });
  return pedacos;
}

/**
 * Devolve uma cópia dos nós com os e-mails linkados, ou os próprios nós (mesma
 * referência) quando não há nada a fazer.
 */
export function linkarEmails(nodes: Node[]): Node[] {
  let mudou = false;
  const saida: Node[] = [];
  for (const n of nodes) {
    if (isLeaf(n)) {
      const partes = n.text.includes('@') ? dividirFolha(n) : null;
      if (partes) {
        mudou = true;
        saida.push(...partes);
      } else {
        saida.push(n);
      }
      continue;
    }
    if (LINK_TYPES.has(n.type || '') || !Array.isArray(n.children)) {
      saida.push(n);
      continue;
    }
    const filhos = linkarEmails(n.children);
    if (filhos !== n.children) {
      mudou = true;
      saida.push({ ...n, children: filhos });
    } else {
      saida.push(n);
    }
  }
  return mudou ? saida : nodes;
}

interface SlateViewProps {
  data?: { value?: Node[]; [k: string]: unknown };
  [k: string]: unknown;
}

export const withEmailsClicaveis = <P extends SlateViewProps>(
  View: React.ComponentType<P>,
): React.FC<P> => {
  const EmailsClicaveis: React.FC<P> = (props) => {
    const value = props?.data?.value;
    if (!Array.isArray(value)) return <View {...props} />;
    const novo = linkarEmails(value);
    if (novo === value) return <View {...props} />;
    return <View {...props} data={{ ...props.data, value: novo }} />;
  };
  EmailsClicaveis.displayName = `withEmailsClicaveis(${
    View?.displayName || View?.name || 'Block'
  })`;
  return EmailsClicaveis;
};

export default withEmailsClicaveis;
